import AbstractListView from '@UI/Store/AbstractListView';
import type DocumentTypeView from '@UI/Store/DocumentTypes/Views/DocumentTypeView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `list` is always ordered system types first, then free types by French label (`localeCompare`) —
 * the constructor re-sorts on every construction, so every mutator below naturally produces a
 * reordered instance without repeating the sort itself. `accountId` scopes the whole catalogue.
 */
class DocumentTypesListView extends AbstractListView<DocumentTypeView> {
	readonly accountId: number;

	constructor(accountId: number, list: readonly DocumentTypeView[]) {
		super(DocumentTypesListView.ordered(list), 'DocumentTypesListView');

		this.accountId = accountId;

		Object.freeze(this);
	}

	private static ordered(list: readonly DocumentTypeView[]): readonly DocumentTypeView[] {
		return [...list].sort((first, second) => {
			if (first.isSystem !== second.isSystem) {
				return first.isSystem ? -1 : 1;
			}

			return first.label.localeCompare(second.label, 'fr');
		});
	}

	/** "Type inconnu" covers a stale or cross-account identifier: never thrown, always a label. */
	labelOf(documentTypeId: number): string {
		return this.findById(documentTypeId)?.label ?? 'Type inconnu';
	}

	/** Reserved for choice screens (dropdowns, launch options) — never for resolving a past document's label. */
	activeTypes(): readonly DocumentTypeView[] {
		return this.list.filter((documentType) => !documentType.isArchived);
	}

	archivedTypes(): readonly DocumentTypeView[] {
		return this.list.filter((documentType) => documentType.isArchived);
	}

	add(documentType: DocumentTypeView): DocumentTypesListView {
		return new DocumentTypesListView(this.accountId, [...this.list, documentType]);
	}

	replace(documentType: DocumentTypeView): DocumentTypesListView {
		return new DocumentTypesListView(
			this.accountId,
			this.list.map((entry) => (entry.id === documentType.id ? documentType : entry)),
		);
	}

	markArchived(documentTypeId: number, archivedAt: string): DocumentTypesListView {
		return new DocumentTypesListView(
			this.accountId,
			this.list.map((entry) =>
				entry.id === documentTypeId ? entry.withArchivedAt(archivedAt) : entry,
			),
		);
	}

	markRestored(documentTypeId: number): DocumentTypesListView {
		return new DocumentTypesListView(
			this.accountId,
			this.list.map((entry) => (entry.id === documentTypeId ? entry.withArchivedAt(null) : entry)),
		);
	}
}

export default DocumentTypesListView;
